import React, { useMemo } from 'react';
import { Box, CircularProgress, IconButton, styled, Typography } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useLocalAudioToggle, useMutedParticipants, useVideo } from 'hooks';
import { MicIcon, MicOffIcon } from 'assets';
import { ControlTooltip } from 'components';
import { IMutedParticipantsContext } from 'context';

import styles from './ToggleAudioButton.module.scss';

type ToggleAudioButtonProps = {
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  withTooltip?: boolean;
  withLabel?: boolean;
};

const MicIconStyled = styled(MicIcon)(() => ({
  color: '#fff',
}));

const MicIconOffStyled = styled(MicOffIcon)(() => ({
  color: '#fff',
}));

export function ToggleAudioButton({
  disabled,
  className,
  iconClassName,
  withTooltip,
  withLabel,
}: ToggleAudioButtonProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { isCurrentParticipantMuted }: IMutedParticipantsContext = useMutedParticipants();
  const [isAudioEnabled, toggleAudioEnabled, isAudioToggling] = useLocalAudioToggle();
  const { localTracks } = useVideo();
  const hasAudioTrack = localTracks.some((track) => track.kind === 'audio');

  const tooltipMessage = useMemo(() => {
    let tooltipMessage = '';

    if (withTooltip) {
      tooltipMessage = isCurrentParticipantMuted ? t('youHaveBeenMuted') : isAudioEnabled ? t('mute') : t('unmute');
    }

    return tooltipMessage;
  }, [withTooltip, isAudioEnabled, isCurrentParticipantMuted, t]);

  const sx = useMemo(
    () => ({
      backgroundColor: `${isAudioEnabled ? 'rgb(0 0 0 / 50%)' : '#FF3D3D'} !important`,
      '&:hover': {
        backgroundColor: isAudioEnabled ? 'rgb(0 0 0 / 50%)' : '#FF3D3D',
      },
    }),
    [isAudioEnabled],
  );

  return (
    <ControlTooltip title={tooltipMessage} isDisabled={!isAudioEnabled}>
      <Box component='span' display='block' width={withLabel ? '100%' : undefined}>
        <IconButton
          disabled={!hasAudioTrack || disabled || isCurrentParticipantMuted || isAudioToggling}
          className={classnames(
            styles.button,
            {
              [styles.deviceButton]: !withLabel,
              [styles.deviceButtonWithLabel]: withLabel,
              [styles.disabled]: !hasAudioTrack || disabled || isCurrentParticipantMuted || isAudioToggling,
            },
            className,
          )}
          sx={
            !withLabel
              ? sx
              : {
                  justifyContent: 'flex-start',
                  width: '100%',
                }
          }
          onClick={toggleAudioEnabled}
          aria-label={t('aria.toggleMicrophone')}
        >
          {isAudioToggling ? (
            <Box display='flex' alignItems='center' justifyContent='center' width={24} height={24}>
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            </Box>
          ) : (
            <>
              {isAudioEnabled ? (
                <MicIconStyled
                  className={classnames(styles.icon, iconClassName)}
                  sx={withLabel ? { color: '#3EB157' } : {}}
                />
              ) : (
                <MicIconOffStyled
                  className={classnames(styles.icon, iconClassName)}
                  sx={withLabel ? { color: '#FF3D3D' } : {}}
                />
              )}
            </>
          )}
          {withLabel && (
            <Typography
              component='p'
              variant='body2'
              sx={{
                color: 'var(--app-text-default)',
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'keep-all',
                overflowWrap: 'normal',
                textAlign: isRtl ? 'right' : 'left',
                paddingInlineStart: '10px',
              }}
            >
              {t('microphone')}
            </Typography>
          )}
        </IconButton>
      </Box>
    </ControlTooltip>
  );
}
